import { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, ScrollText } from 'lucide-react';
import Starfield from './Starfield';
import { IMAGES } from '../data/studio';

const LINES = ['SIDETRACK', 'AI STUDIOS'];

const MaskedLine = ({ text, delay, className }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: '115%' }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

export default function Hero({ onNavigate }) {
  const [elapsed, setElapsed] = useState(0);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 800], [0, 180]);
  const fgY = useTransform(scrollY, [0, 800], [0, -80]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  useEffect(() => {
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');

  return (
    <section id="top" className="relative min-h-screen flex flex-col overflow-hidden grain-overlay" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={IMAGES.hero}
          alt=""
          className="w-full h-full object-cover opacity-30"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#02040a]/60 via-[#02040a]/40 to-[#02040a]" />
      </motion.div>
      <Starfield className="absolute inset-0 w-full h-full" />

      <motion.div
        style={{ y: fgY, opacity: fade }}
        className="relative z-10 flex-1 flex flex-col justify-center max-w-7xl mx-auto w-full px-6 lg:px-10 pt-28 pb-16"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="font-mono-precision text-xs uppercase tracking-[0.35em] text-cyan-400/90 mb-8"
          data-testid="hero-eyebrow"
        >
          A parent studio · Est. in the void
        </motion.p>

        <h1 className="font-celestial font-light text-slate-100 leading-[0.95]" data-testid="hero-headline">
          <MaskedLine text={LINES[0]} delay={0.5} className="text-5xl sm:text-7xl lg:text-8xl tracking-tight" />
          <MaskedLine text={LINES[1]} delay={0.65} className="text-5xl sm:text-7xl lg:text-8xl tracking-tight text-glow-cyan text-cyan-300" />
        </h1>

        <span className="block overflow-hidden mt-8 max-w-2xl">
          <motion.span
            className="block text-base sm:text-lg text-slate-300/90 leading-relaxed"
            initial={{ y: '110%' }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-tagline"
          >
            Stories, ideas, technology, music — and the places thought can lead.
            A studio fusing deep space physics, synthetic consciousness, software and narrative worlds.
          </motion.span>
        </span>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-wrap items-center gap-4 mt-12"
        >
          <button
            onClick={() => onNavigate('worlds')}
            className="group flex items-center gap-3 bg-cyan-400 text-[#02040a] font-mono-precision text-xs uppercase tracking-[0.2em] px-7 py-4 rounded-full hover:bg-cyan-300 transition-all duration-300 hover:shadow-[0_0_35px_rgba(34,211,238,0.5)]"
            data-testid="hero-enter-worlds-btn"
          >
            Enter the Six Worlds
            <ArrowDown size={15} className="group-hover:translate-y-0.5 transition-transform duration-300" />
          </button>
          <button
            onClick={() => onNavigate('manifesto')}
            className="flex items-center gap-3 border border-slate-700 text-slate-200 font-mono-precision text-xs uppercase tracking-[0.2em] px-7 py-4 rounded-full hover:border-cyan-400/60 hover:text-cyan-300 transition-all duration-300"
            data-testid="hero-manifesto-btn"
          >
            <ScrollText size={15} />
            Read Studio Manifesto
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="relative z-10 border-t border-cyan-500/10 bg-[#02040a]/60 backdrop-blur-xl"
        data-testid="hero-telemetry-ticker"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 grid grid-cols-3 gap-4">
          {[
            { label: 'Cosmic session', value: `T+ ${mm}:${ss}` },
            { label: 'Active thought threads', value: '06 / 06' },
            { label: 'Signal strength', value: '97.4%' },
          ].map((m) => (
            <div key={m.label} className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
              <span className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-slate-500">{m.label}</span>
              <span className="font-mono-precision text-xs text-cyan-300">{m.value}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
